/**
 * Presigned URLs of chat images, keyed by identity + root-relative key. A turn that rewrites,
 * moves or deletes a file evicts its entry (via the `files_changed` window event), so the next
 * render presigns again; the new query string also bypasses the browser's image cache.
 */
import {tryParseCloudPath} from '@/shared/utils/cloudPath'
import {subscribeChatFilesChanged, type FileChange, type FileChangeOp} from '../filesChanged'

/** Ops that change what a path holds (`folder_created` adds an empty folder, so no image changes). */
const EVICTING_OPS: ReadonlySet<FileChangeOp> = new Set([
  'created',
  'updated',
  'moved',
  'copied',
  'deleted',
])

/**
 * Tells whether a key was just evicted. `files_changed` carries no identity (it is always the
 * signed-in user's), so a match evicts the key under every identity.
 */
export type EvictionPredicate = (key: string) => boolean
type EvictionListener = (isEvicted: EvictionPredicate) => void

const cache = new Map<string, {key: string; url: string}>()
const listeners = new Set<EvictionListener>()
let unsubscribeWindow: (() => void) | null = null

function cacheKey(identityId: string, key: string): string {
  return `${identityId}\u0000${key}`
}

function ensureSubscribed(): void {
  if (unsubscribeWindow || typeof window === 'undefined') return
  unsubscribeWindow = subscribeChatFilesChanged(evictPresignedUrlsForChanges)
}

export function getPresignedUrl(identityId: string, key: string): string | undefined {
  ensureSubscribed()
  return cache.get(cacheKey(identityId, key))?.url
}

export function setPresignedUrl(identityId: string, key: string, url: string): void {
  ensureSubscribed()
  cache.set(cacheKey(identityId, key), {key, url})
}

export function deletePresignedUrl(identityId: string, key: string): void {
  cache.delete(cacheKey(identityId, key))
}

/** Root-relative paths touched by the changes; folders end in `/` and match everything below. */
function changedRels(changes: readonly FileChange[]): {files: Set<string>; folders: string[]} {
  const files = new Set<string>()
  const folders: string[] = []
  const add = (path: string | undefined) => {
    const parsed = path ? tryParseCloudPath(path) : null
    if (!parsed) return
    if (parsed.isFolder) folders.push(parsed.rel)
    else files.add(parsed.rel)
  }
  for (const change of changes) {
    if (!EVICTING_OPS.has(change.op)) continue
    add(change.path)
    add(change.from)
  }
  return {files, folders}
}

/** Drops the cached URLs of every changed path (for all identities) and tells mounted images. */
export function evictPresignedUrlsForChanges(changes: readonly FileChange[]): void {
  const {files, folders} = changedRels(changes)
  if (files.size === 0 && folders.length === 0) return
  const matches = (key: string) =>
    files.has(key) || folders.some((folder) => folder === '' || key.startsWith(folder))
  for (const [k, entry] of [...cache.entries()]) {
    if (matches(entry.key)) cache.delete(k)
  }
  listeners.forEach((listener) => listener(matches))
}

/** Mounted images subscribe to re-presign when their entry is evicted. Returns an unsubscribe. */
export function subscribePresignedUrlEvictions(listener: EvictionListener): () => void {
  ensureSubscribed()
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function resetPresignedUrlCacheForTests(): void {
  cache.clear()
  listeners.clear()
  unsubscribeWindow?.()
  unsubscribeWindow = null
}
